import { useState, useMemo, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { useHistory } from 'react-router-dom'
import classNames from 'classnames'
import { toast } from 'react-toastify'

import { API, API_ROUTER } from '../../../../api'

import { isAuthenticated } from '../../../../helpers'

import { fulldate } from './constant/date'

import banerAsia from '../../../../assets/images/event-asia-baner.png'
import asiaLogo from '../../../../assets/asia-logo.jpeg'
import calendar from '../../../../assets/events/calendar.svg'
import point from '../../../../assets/events/point.svg'

import styles from './styles.module.scss'

function EventHeader({ 
  event, 
  isFollow = false,
  onNavigateToUrl,
  onFollowEvent,
  onUnfollowEvent,
  onLoginRequired
}) {
  const [star, tongleStar] = useState(isFollow)
  const history = useHistory()

  const bannerImage = useMemo(() => 
    event?.profilePageHeaderBanner || banerAsia, 
    [event?.profilePageHeaderBanner]
  )

  const eventLogo = useMemo(() => 
    event?.logo || asiaLogo, 
    [event?.logo]
  )

  const eventName = useMemo(() => 
    event?.name || 'EVENT NAME', 
    [event?.name]
  )

  const communityUrl = useMemo(() => 
    `/community/${event?.community}/events`, 
    [event?.community]
  )

  const hasEventType = useMemo(() => !!event?.type, [event?.type])
  const hasStartDate = useMemo(() => !!event?.startDate, [event?.startDate])
  const hasLocation = useMemo(() => !!event?.location, [event?.location])
  const hasStatus = useMemo(() => !!event?.status, [event?.status])
  const isUserAuthenticated = useMemo(() => isAuthenticated(), [])

  const bannerStyle = useMemo(() => ({ 
    '--banner-bg': `url(${bannerImage})` 
  }), [bannerImage])

  const eventLogoClassName = useMemo(() => 
    classNames(styles.cP, styles.eventLogo),
    []
  )

  const dateAndPlaceContent = useMemo(() => {
    if (!hasStartDate) return null

    return (
      <>
        {fulldate(event.startDate, event.endDate)}
        <img
          className={styles.events__date__image}
          src={point}
          alt=''
          width='17.4'
          height='20'
        />
      </>
    )
  }, [hasStartDate, event?.startDate, event?.endDate])

  const handleGoToUrl = useCallback((url) => {
    if (onNavigateToUrl) {
      onNavigateToUrl(url)
    } else {
      history.push(url)
      if (!isUserAuthenticated) {
        localStorage.removeItem('tournamentId')
        localStorage.removeItem('communityId')
        localStorage.removeItem('profileUrl')
        localStorage.setItem('eventId', event?.id)
      }
    }
  }, [onNavigateToUrl, history, event?.id, isUserAuthenticated])

  const handleFollowEvent = useCallback(() => {
    if (onFollowEvent) {
      onFollowEvent(event?.id)
    } else {
      API.request({
        ...API_ROUTER.follow.followEvent,
        data: {
          eventId: event?.id,
        },
      })
        .then(() => {
          toast.success('You followed this event')
          tongleStar(true)
        })
        .catch(err => console.log(err))
    }
  }, [onFollowEvent, event?.id])

  const handleUnfollowEvent = useCallback(() => {
    if (onUnfollowEvent) {
      onUnfollowEvent(event?.id)
    } else {
      API.request({
        ...API_ROUTER.follow.stopFollowEvent,
        pathKeys: {
          eventId: event?.id,
        },
      })
        .then(() => {
          toast.success('You unfollowed this event')
          tongleStar(false)
        })
        .catch(err => console.log(err))
    }
  }, [onUnfollowEvent, event?.id])

  const handleToggleFollow = useCallback(() => {
    if (star) {
      handleUnfollowEvent()
    } else {
      handleFollowEvent()
    }
  }, [star, handleFollowEvent, handleUnfollowEvent])

  const handleLoginClick = useCallback(() => {
    if (onLoginRequired) {
      onLoginRequired()
    } else {
      handleGoToUrl('/login')
    }
  }, [onLoginRequired, handleGoToUrl])

  const handleCommunityClick = useCallback(() => {
    handleGoToUrl(communityUrl)
  }, [handleGoToUrl, communityUrl])

  const statusDisplay = useMemo(() => {
    if (!hasStatus) return null

    return (
      <div className={styles.statusDisplay}>
        <span className={styles.statusLabel}>
          <FormattedMessage id='game.tournaments.table.status' />
        </span>
        <span className={styles.statusValue}>
          {event.status}
        </span>
      </div>
    )
  }, [hasStatus, event?.status])

  const loginMessage = useMemo(() => {
    if (isUserAuthenticated) return null

    return (
      <h3 className={styles.loginMessage}>
        <FormattedMessage id='navigation.login' />
      </h3>
    )
  }, [isUserAuthenticated])

  const followButton = useMemo(() => {
    if (!isUserAuthenticated) {
      return (
        <button
          type="button"
          className={styles.eventsButton}
          onClick={handleLoginClick}
          aria-label="Login to follow this event"
        >
          <span className={styles.eventsButtonText}>
            <FormattedMessage id='global.buttons.login' />
          </span>
        </button>
      )
    }

    return !star ? (
      <button
        type="button"
        className={styles.eventsButton}
        onClick={handleToggleFollow}
        aria-label="Follow this event"
      >
        <span className={styles.eventsButtonText}>
          <FormattedMessage id='id.follow' />
        </span>
      </button>
    ) : (
      <button
        type="button"
        className={styles.eventsButton}
        onClick={handleToggleFollow}
        aria-label="Unfollow this event"
      >
        <span className={styles.eventsButtonText}>unfollow</span>
      </button>
    )
  }, [isUserAuthenticated, star, handleLoginClick, handleToggleFollow])

  return (
    <>
      <div className={styles.top}>
        <div
          className={styles.topBanner}
          style={bannerStyle}
        >
          <div className={styles.body}>
            <div />
            <img
              src={eventLogo}
              alt='logo'
              className={eventLogoClassName}
            />
          </div>
        </div>
        <div className={classNames(styles.news, styles.eventHeaderBg)}>
          <div>
            <h2>{eventName}</h2> <br />{' '}
            <h3>
              {hasEventType && (
                <>
                  <FormattedMessage id='community.type' /> {event.type}
                </>
              )}
            </h3>
            <article className={styles.events__date__and__place}>
              <img
                className={styles.events__date__image}
                src={calendar}
                alt='calendar'
                width='23'
                height='31'
              />
              {dateAndPlaceContent}
              {hasLocation && (
                <p className={styles.events__date__text}>{event.location}</p>
              )}
            </article>
            {loginMessage}
          </div>

          {statusDisplay}
        </div>
        <div className={styles.button}>
          <article className={styles.eventsButtonsAndStatic}>
            <button
              type="button"
              className={styles.eventsButton}
              onClick={handleCommunityClick}
              aria-label="Go to community page"
            >
              <span className={styles.eventsButtonText}>
                <FormattedMessage id='navigation.community' />
              </span>
            </button>

            {followButton}
          </article>
        </div>
      </div>
    </>
  )
}
export default EventHeader
